import { useState, useEffect, useRef } from 'react';
import { Bell, X, Check, CheckCheck, ChevronRight, TrendingUp, TrendingDown, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';

const NotificationDropdown = () => {
  const { isNotificationOpen, closeNotifications, toggleNotifications } = useNotification();
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const dropdownRef = useRef(null);

  // Fetch notifications when dropdown opens (only if authenticated)
  useEffect(() => {
    if (isNotificationOpen && isAuthenticated) {
      fetchNotifications();
    }
  }, [isNotificationOpen, isAuthenticated]);

  // Fetch unread count on mount and periodically (only if authenticated)
  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }
    
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000); // Every minute
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        closeNotifications();
        setSelectedNotification(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [closeNotifications]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await api.get('/notifications/web?limit=20');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      // Don't show error toast for auth errors (user not logged in)
      if (error.response?.status !== 401) {
        toast.error('Failed to load notifications');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get('/notifications/web/unread-count');
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      // Silently fail - user might not be logged in
      console.debug('Could not fetch unread count:', error.message);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/web/${notificationId}/read`);
      // Update local state
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/web/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
  };

  const handleCloseDetail = () => {
    // Mark as read when closing the detail view
    if (selectedNotification && !selectedNotification.read) {
      markAsRead(selectedNotification.notification_id);
    }
    setSelectedNotification(null);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const formatFullTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getNotificationIcon = (notification) => {
    const type = notification.type || notification.data?.type;
    
    // Check for rate direction from data
    const change = parseFloat(notification.data?.change);
    
    if (type === 'gold_rate_alert' || type === 'daily_snapshot') {
      if (change > 0) {
        return (
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
        );
      } else if (change < 0) {
        return (
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <TrendingDown className="w-5 h-5 text-red-600" />
          </div>
        );
      }
      return (
        <div className="w-10 h-10 rounded-full bg-[#B8860B]/20 flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-[#B8860B]" />
        </div>
      );
    }
    
    return (
      <div className="w-10 h-10 rounded-full bg-[#004D40]/10 flex items-center justify-center">
        <Bell className="w-5 h-5 text-[#004D40]" />
      </div>
    );
  };

  // Render notification detail modal
  const renderNotificationDetail = () => {
    if (!selectedNotification) return null;

    const change = parseFloat(selectedNotification.data?.change);
    const isRateAlert = selectedNotification.type === 'gold_rate_alert' || selectedNotification.type === 'daily_snapshot';

    return (
      <div className="absolute inset-0 bg-white rounded-xl z-10 flex flex-col">
        {/* Detail Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-[#004D40]">Notification Details</h3>
          <button
            onClick={handleCloseDetail}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Detail Content */}
        <div className="flex-1 p-4 overflow-y-auto">
          {/* Icon and Title */}
          <div className="flex items-start gap-4 mb-4">
            {getNotificationIcon(selectedNotification)}
            <div className="flex-1">
              <h4 className="text-base font-semibold text-[#004D40]">{selectedNotification.title}</h4>
              <div className="flex items-center gap-2 mt-1">
                <Clock className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-xs text-gray-500">{formatFullTimestamp(selectedNotification.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-700 leading-relaxed">{selectedNotification.body}</p>
          </div>

          {/* Rate Info (if applicable) */}
          {isRateAlert && selectedNotification.data?.rate && (
            <div className="bg-[#F5E6C8]/50 rounded-lg p-4 border border-[#B8860B]/30">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[#004D40] font-medium">Current Rate</span>
                <span className="text-lg font-bold text-[#004D40]">₹{parseFloat(selectedNotification.data.rate).toFixed(2)}</span>
              </div>
              {change !== 0 && !isNaN(change) && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#004D40]/70">Change</span>
                  <span className={`text-sm font-medium ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {change > 0 ? '+' : ''}₹{change.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Type Badge */}
          <div className="mt-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[#004D40]/10 text-[#004D40]">
              {isRateAlert ? 'Gold Rate Alert' : 'Notification'}
            </span>
          </div>
        </div>

        {/* Close Button */}
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={handleCloseDetail}
            className="w-full py-2.5 bg-[#004D40] text-white font-medium rounded-lg hover:bg-[#00302B] transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button 
        onClick={toggleNotifications}
        className="relative p-1 text-white hover:text-[#FFD700] transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-7 h-7" strokeWidth={1.5} />
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isNotificationOpen && (
        <div className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#004D40] to-[#00302B]">
            <h3 className="text-base font-semibold text-white">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-1.5 text-xs text-white/80 hover:text-white transition-colors"
                data-testid="mark-all-read"
              >
                <CheckCheck className="w-4 h-4" />
                Mark all read
              </button>
            )}
          </div>

          {/* Content */}
          <div className="relative max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-[#004D40] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                  <Bell className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-sm text-gray-500">No notifications yet</p>
                <p className="text-xs text-gray-400 mt-1">You&apos;ll see gold rate alerts here</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {notifications.map((notification) => (
                  <button
                    key={notification.notification_id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full flex items-start gap-3 p-4 text-left hover:bg-gray-50 transition-colors ${
                      !notification.read ? 'bg-[#F5E6C8]/20' : ''
                    }`}
                    data-testid={`notification-item-${notification.notification_id}`}
                  >
                    {/* Icon */}
                    {getNotificationIcon(notification)}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!notification.read ? 'font-semibold text-[#004D40]' : 'font-medium text-gray-700'}`}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <span className="w-2 h-2 rounded-full bg-[#B8860B] shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{notification.body}</p>
                      <p className="text-xs text-gray-400 mt-1">{formatTimestamp(notification.created_at)}</p>
                    </div>

                    {/* Arrow */}
                    <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 mt-1" />
                  </button>
                ))}
              </div>
            )}

            {/* Notification Detail Overlay */}
            {renderNotificationDetail()}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center">
                Showing latest {notifications.length} notifications
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
